import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input } from '@/components/ui';
import type { AdminVoidRequest } from '@/lib/api-admin/types';
import { voidFormSchema, type VoidFormValues } from '../lib/paymentSchema';

export interface VoidActionModalProps {
  paymentNo: string;
  isOpen: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (body: AdminVoidRequest) => void;
  onCancel: () => void;
}

export const VoidActionModal = ({
  paymentNo,
  isOpen,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
}: VoidActionModalProps) => {
  const { t } = useTranslation('payments');
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VoidFormValues>({
    resolver: zodResolver(voidFormSchema),
    defaultValues: { reason: '', detail: '' },
  });

  useEffect(() => {
    if (!isOpen) reset({ reason: '', detail: '' });
  }, [isOpen, reset]);

  const submit = handleSubmit((values) => {
    const body: AdminVoidRequest = {
      reason: values.reason.trim(),
      detail:
        values.detail && values.detail.length > 0 ? values.detail.trim() : null,
    };
    onSubmit(body);
  });

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle>{t('payments.voidModal.title')}</DialogTitle>
        <DialogDescription>
          {t('payments.voidModal.subject', { paymentNo })}
        </DialogDescription>
        <p
          className="text-sm"
          style={{ color: 'var(--signal-warn)' }}
          role="status"
        >
          {t('payments.voidModal.warning')}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-col">
            <label
              htmlFor="void-reason"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('payments.voidModal.reason')}
            </label>
            <Input id="void-reason" maxLength={200} {...register('reason')} />
            {errors.reason ? (
              <p
                className="mt-1 text-xs"
                role="alert"
                style={{ color: 'var(--status-danger-fg)' }}
              >
                {t('payments.voidModal.reasonRequired')}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="void-detail"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('payments.voidModal.detail')}
            </label>
            <textarea
              id="void-detail"
              className="rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor: 'var(--surface-1)',
                background: 'var(--surface-0)',
                color: 'var(--content-strong)',
              }}
              rows={3}
              maxLength={500}
              {...register('detail')}
            />
          </div>

          {errorMessage ? (
            <p className="text-sm" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('payments.actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="danger"
              size="md"
              disabled={isSubmitting}
              data-action="void-confirm"
            >
              {isSubmitting
                ? t('payments.voidModal.submitting')
                : t('payments.voidModal.confirm')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
